const API_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY;

export interface ForwardGeocodeResult {
  formattedAddress: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  centroid: { latitude: number; longitude: number };
}

type GoogleComponent = { long_name: string; short_name: string; types: string[] };

function pick(components: GoogleComponent[], type: string, short = false): string {
  const c = components.find((cc) => cc.types.includes(type));
  if (!c) return '';
  return short ? c.short_name : c.long_name;
}

export async function forwardGeocode(address: string): Promise<ForwardGeocodeResult[]> {
  if (!API_KEY) return [];
  const q = address.trim();
  if (!q) return [];

  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(q)}&key=${API_KEY}`
    );
    const data = await res.json();
    if (data.status !== 'OK' || !Array.isArray(data.results)) return [];

    return data.results.map((r: unknown): ForwardGeocodeResult => {
      const row = r as {
        formatted_address?: string;
        address_components?: GoogleComponent[];
        geometry?: { location?: { lat: number; lng: number } };
      };
      const components = row.address_components ?? [];
      const street = [pick(components, 'street_number'), pick(components, 'route')]
        .filter(Boolean)
        .join(' ');
      const city =
        pick(components, 'locality') ||
        pick(components, 'sublocality') ||
        pick(components, 'postal_town');
      const state = pick(components, 'administrative_area_level_1', true);
      const zip = pick(components, 'postal_code');
      const loc = row.geometry?.location;
      return {
        formattedAddress: row.formatted_address ?? '',
        street,
        city,
        state,
        zip,
        centroid: {
          latitude: typeof loc?.lat === 'number' ? loc.lat : 0,
          longitude: typeof loc?.lng === 'number' ? loc.lng : 0,
        },
      };
    });
  } catch {
    return [];
  }
}

// --- Places Autocomplete (New) ---
//
// As-you-type address suggestions, the primary way to set an observation
// location. Uses the "Places API (New)" REST endpoints with a per-session
// token: autocomplete calls + the final placeDetails call sharing one token
// are billed as a single session. Enable "Places API (New)" on the same
// Google Cloud key used for maps/geocoding.

// Opaque per-session token grouping autocomplete keystrokes + the final
// details fetch into one billed session. Uniqueness (not cryptographic
// strength) is all that's required.
export function newSessionToken(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export interface PlaceSuggestion {
  placeId: string;
  // Bold primary line (e.g. "123 Main St") + secondary (e.g. "Big Bear Lake, CA").
  primaryText: string;
  secondaryText: string;
}

export async function placeAutocomplete(
  input: string,
  sessionToken: string
): Promise<PlaceSuggestion[]> {
  if (!API_KEY) return [];
  const q = input.trim();
  if (!q) return [];

  try {
    const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
      },
      body: JSON.stringify({ input: q, sessionToken, includedRegionCodes: ['us'] }),
    });
    const data = await res.json();
    if (!Array.isArray(data.suggestions)) return [];

    return data.suggestions
      .map((s: unknown): PlaceSuggestion | null => {
        const pred = (s as { placePrediction?: unknown }).placePrediction as
          | {
              placeId?: string;
              structuredFormat?: {
                mainText?: { text?: string };
                secondaryText?: { text?: string };
              };
              text?: { text?: string };
            }
          | undefined;
        if (!pred?.placeId) return null;
        return {
          placeId: pred.placeId,
          primaryText: pred.structuredFormat?.mainText?.text ?? pred.text?.text ?? '',
          secondaryText: pred.structuredFormat?.secondaryText?.text ?? '',
        };
      })
      .filter((p: PlaceSuggestion | null): p is PlaceSuggestion => p !== null);
  } catch {
    return [];
  }
}

export interface PlaceDetailsResult {
  formattedAddress: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  centroid: { latitude: number; longitude: number };
}

// Places API (New) returns components as { longText, shortText, types }.
type PlaceComponent = { longText?: string; shortText?: string; types?: string[] };

function pickPlace(components: PlaceComponent[], type: string, short = false): string {
  const c = components.find((cc) => cc.types?.includes(type));
  if (!c) return '';
  return (short ? c.shortText : c.longText) ?? '';
}

export async function placeDetails(
  placeId: string,
  sessionToken: string
): Promise<PlaceDetailsResult | null> {
  if (!API_KEY) return null;

  try {
    const res = await fetch(
      `https://places.googleapis.com/v1/places/${encodeURIComponent(
        placeId
      )}?sessionToken=${encodeURIComponent(sessionToken)}`,
      {
        headers: {
          'X-Goog-Api-Key': API_KEY,
          'X-Goog-FieldMask': 'formattedAddress,location,addressComponents',
        },
      }
    );
    const data = await res.json();
    const loc = data.location as { latitude?: number; longitude?: number } | undefined;
    if (typeof loc?.latitude !== 'number' || typeof loc?.longitude !== 'number') return null;

    const components: PlaceComponent[] = Array.isArray(data.addressComponents)
      ? data.addressComponents
      : [];
    const street = [pickPlace(components, 'street_number'), pickPlace(components, 'route')]
      .filter(Boolean)
      .join(' ');
    const city =
      pickPlace(components, 'locality') ||
      pickPlace(components, 'sublocality') ||
      pickPlace(components, 'postal_town');
    const state = pickPlace(components, 'administrative_area_level_1', true);
    const zip = pickPlace(components, 'postal_code');

    return {
      formattedAddress: typeof data.formattedAddress === 'string' ? data.formattedAddress : '',
      street,
      city,
      state,
      zip,
      centroid: { latitude: loc.latitude, longitude: loc.longitude },
    };
  } catch {
    return null;
  }
}

/**
 * "977 Club View Drive, Big Bear Lake, CA". Empty parts drop out with their
 * comma. Before Sept 2026 there was no comma between street and city, so
 * posts saved earlier read "977 Club View Drive Big Bear Lake, CA".
 */
export function formatShortAddress(parts: {
  streetNumber?: string;
  street?: string;
  city?: string;
  state?: string;
}): string | null {
  const street = [parts.streetNumber, parts.street].filter(Boolean).join(' ');
  return [street, parts.city, parts.state].filter(Boolean).join(', ') || null;
}

export async function reverseGeocode(latitude: number, longitude: number): Promise<string | null> {
  if (!API_KEY) return null;

  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${API_KEY}`
    );
    const data = await res.json();

    if (data.status !== 'OK' || !data.results?.length) return null;

    const components = data.results[0].address_components as GoogleComponent[];

    const street = pick(components, 'route');
    const streetNumber = pick(components, 'street_number');
    const city = pick(components, 'locality');
    const state = pick(components, 'administrative_area_level_1', true);

    return formatShortAddress({ streetNumber, street, city, state });
  } catch {
    return null;
  }
}
